import React from "react";
import axios from "axios";
import validator from 'validator';
import { v4 as uuidv4 } from 'uuid';
import { notifications } from '@mantine/notifications';
import { Drawer, Button, SegmentedControl, NativeSelect, Group, Text, ActionIcon } from '@mantine/core';
import { IconRefresh, IconArrowLeft } from '@tabler/icons-react';

import { useLoginContextSU } from "../../context/loginContextSU";
import { BE_API_SU_ONE, utils, role } from "../../utils/globalVariables";
import { regexLimitChar, regexNoSpace } from "../../utils/globalFunctions";

import InputComponent from "../../components/general/InputComponent";
import InputWrapperCustom from "../../components/general/InputWrapperCustom";
import ModalResponse from "../../components/general/ModalResponse";

const SUDataUserAdd = ({
    stateShow,
    fnShow,
    fnSuccessAdd,
}) => {

    const mountedRef = React.useRef(true);
    const { loginContextSU, handleSetLoginContextSU } = useLoginContextSU();

    const { super_user, ...newRole } = role;

    const subLevelSelector = (stateUserlevel) => {
        const inSubLevel = newRole[stateUserlevel].subLevel;

        const res = Object.keys(inSubLevel).map(subRoleName => ({
            value: inSubLevel[subRoleName].slugSubLevel,
            label: inSubLevel[subRoleName].caption,
        }));

        return (Array.isArray(res) && (res.length > 0)) ? res[0].value : '';
    }

    const notificationId = React.useRef({
        errorSubmitData: 'SUDataUserAdd-1',
        successSubmitData: 'SUDataUserAdd-2',
    })

    const constUserName = {
        id: 'add--userName',
        label: 'Nama',
        withAsterisk: true,
        placeholder: 'Nama',
        value: '',
        description: '',
        error: '',
    }

    const constUserGender = {
        id: 'add--userGender',
        label: 'Gender',
        withAsterisk: true,
        placeholder: 'Gender',
        value: '1',
        description: '',
        error: '',
    }

    const constUserLevel = {
        id: 'add--userLevel',
        label: 'Jabatan',
        withAsterisk: true,
        placeholder: 'Jabatan',
        value: role.owner.slugLevel,
        description: '',
        error: '',
    }

    const constUserSubLevel = {
        id: 'add--userSubLevel',
        label: 'Sub Jabatan',
        withAsterisk: true,
        placeholder: 'Sub Jabatan',
        value: subLevelSelector(role.owner.slugLevel),
        description: '',
        error: '',
    }

    const constUserEmail = {
        id: 'add--userEmail',
        label: 'Email (Opsional)',
        withAsterisk: false,
        placeholder: 'Email',
        value: '',
        description: '',
        error: '',
    }

    const constDefaultUserName = {
        id: 'add--defaultUserName',
        label: `Default Username (${utils.minCharUsername}-${utils.maxCharUsername} digit)`,
        withAsterisk: true,
        placeholder: 'Default Username',
        value: '',
        description: 'Default username dapat diubah kemudian oleh user yang bersangkutan.',
        error: '',
    }

    const constDefaultUserPassword = {
        id: 'add--defaultUserPassword',
        label: `Default Password (${utils.minCharPassword}-${utils.maxCharPassword} digit)`,
        withAsterisk: true,
        placeholder: 'Default Password',
        value: '',
        description: 'Default password dapat diubah kemudian oleh user yang bersangkutan.',
        error: '',
    }

    const [data, setData] = React.useState({
        userName: constUserName,
        userGender: constUserGender,
        userLevel: constUserLevel,
        userSubLevel: constUserSubLevel,
        userEmail: constUserEmail,
        userDefaultUsername: constDefaultUserName,
        userDefaultPassword: constDefaultUserPassword,

        submitLoading: false,

        modalResponse: {
            show: false,
            type: utils.modalResponse.warning,
            hideOnClickOutSide: false,
            content: ''
        }
    });

    const resetData = () => {
        setData(d => ({
            ...d,
            userName: constUserName,
            userGender: constUserGender,
            userEmail: constUserEmail,
            userLevel: constUserLevel,
            userSubLevel: constUserSubLevel,
            userDefaultUsername: constDefaultUserName,
            userDefaultPassword: constDefaultUserPassword
        }))
    }

    React.useEffect(() => {
        return () => {
            mountedRef.current = false;
        }
    }, [])

    const subLevelList = (stateLevelSlug) => {
        try {
            const subLevelThings = newRole[stateLevelSlug].subLevel;

            const res = Object.keys(subLevelThings).map(subRoleName => ({
                value: subLevelThings[subRoleName].slugSubLevel,
                label: subLevelThings[subRoleName].caption,
            }));

            return res;
        } catch (error) {
            return [];
        }
    }

    const handleSubmit = () => {
        setData(d => ({
            ...d,
            userName: { ...d.userName, error: '' },
            userGender: { ...d.userGender, error: '' },
            userEmail: { ...d.userEmail, error: '' },
            userLevel: { ...d.userLevel, error: '' },
            userSubLevel: { ...d.userSubLevel, error: '' },
            userDefaultUsername: { ...d.userDefaultUsername, error: '' },
            userDefaultPassword: { ...d.userDefaultPassword, error: '' },
        }));

        if (validator.isEmpty(data.userName.value, { ignore_whitespace: true })) {
            setData(d => ({ ...d, userName: { ...d.userName, error: 'Nama User Harus Diisi !' } }));
        } else if (data.userEmail.value && !validator.isEmail(data.userEmail.value)) {
            setData(d => ({ ...d, userEmail: { ...d.userEmail, error: 'Format Email Tidak Valid' } }));
        } else if (validator.isEmpty(data.userLevel.value, { ignore_whitespace: true })) {
            setData(d => ({ ...d, userLevel: { ...d.userLevel, error: 'Jabatan Harus Diisi !' } }));
        } else if (validator.isEmpty(data.userSubLevel.value, { ignore_whitespace: true })) {
            setData(d => ({ ...d, userSubLevel: { ...d.userSubLevel, error: 'Sub Jabatan Harus Diisi !' } }));
        } else if (validator.isEmpty(data.userDefaultUsername.value, { ignore_whitespace: true })) {
            setData(d => ({ ...d, userDefaultUsername: { ...d.userDefaultUsername, error: 'Default Username Harus Diisi !' } }));
        } else if (!validator.isLength(data.userDefaultUsername.value, { min: utils.minCharUsername, max: utils.maxCharUsername })) {
            setData(d => ({ ...d, userDefaultUsername: { ...d.userDefaultUsername, error: `Default Username Diisi ${utils.minCharUsername + '-' + utils.maxCharUsername} Digit` } }));
        } else if (validator.isEmpty(data.userDefaultPassword.value, { ignore_whitespace: true })) {
            setData(d => ({ ...d, userDefaultPassword: { ...d.userDefaultPassword, error: 'Default Password Harus Diisi !' } }));
        } else if (!validator.isLength(data.userDefaultPassword.value, { min: utils.minCharPassword, max: utils.maxCharPassword })) {
            setData(d => ({ ...d, userDefaultPassword: { ...d.userDefaultPassword, error: `Default Password Diisi ${utils.minCharPassword + '-' + utils.maxCharPassword} Digit` } }));
        } else {
            setData(d => ({ ...d, submitLoading: true }));

            const formDT = new FormData();
            const formDTHeader = { 'Content-Type': 'application/json' }

            formDT.append('json_token', loginContextSU.syswebapp);
            formDT.append('nama', data.userName.value);
            formDT.append('gender', data.userGender.value);
            formDT.append('email', data.userEmail.value);
            formDT.append('level', data.userLevel.value);
            formDT.append('sub_level', data.userSubLevel.value);
            formDT.append('username', data.userDefaultUsername.value);
            formDT.append('password', data.userDefaultPassword.value);

            axios({
                method: 'post',
                url: BE_API_SU_ONE + '/add_user',
                data: formDT,
                headers: formDTHeader,
            }).then((response) => {
                if (!mountedRef.current) return null;

                setData(d => ({ ...d, submitLoading: false }));

                if (response.data.flag_status === 'isset') {

                    resetData();
                    fnSuccessAdd();

                    notifications.show({
                        id: notificationId.current.successSubmitData,
                        title: utils.notification.success.caption,
                        message: 'Berhasil Submit Data.',
                        color: utils.notification.success.color,
                        icon: utils.notification.success.icon,
                        withBorder: true,
                        autoClose: true,
                    })
                }

                if (response.data.flag_status === 'error') {
                    notifications.show({
                        id: notificationId.current.errorSubmitData,
                        title: utils.notification.warning.caption,
                        message: response.data.pesan,
                        color: utils.notification.warning.color,
                        icon: utils.notification.warning.icon,
                        withBorder: true,
                        autoClose: true,
                    })
                }

                if (response.data.flag_status === 'duplicate-username') {
                    setData(d => ({
                        ...d,
                        modalResponse: {
                            ...d.modalResponse,
                            type: utils.modalResponse.warning,
                            hideOnClickOutSide: false,
                            content: `Username ${response.data.suspect} sudah pernah didaftarkan. Coba pilihan lain.`,
                            show: true
                        }
                    }))
                }

                if (response.data.flag_status === 'duplicate-email') {
                    setData(d => ({
                        ...d,
                        modalResponse: {
                            ...d.modalResponse,
                            type: utils.modalResponse.warning,
                            hideOnClickOutSide: false,
                            content: `Email ${response.data.suspect} sudah pernah didaftarkan.`,
                            show: true
                        }
                    }))
                }

                if (response.data.flag_status === 'invalid-json-token') {
                    handleSetLoginContextSU(null);
                }

                if (response.data.flag_status === 'user-inactive') {
                    handleSetLoginContextSU(null);
                }
            }).catch(error => {
                if (!mountedRef.current) return null;

                setData(d => ({ ...d, submitLoading: false }));

                notifications.show({
                    id: notificationId.current.errorSubmitData,
                    title: utils.notification.warning.caption,
                    message: error.message,
                    color: utils.notification.warning.color,
                    icon: utils.notification.warning.icon,
                    withBorder: true,
                    autoClose: false,
                })
            })
        }
    }

    return (
        <React.Fragment>
            <Drawer
                opened={stateShow}
                onClose={fnShow}
                withCloseButton={false}
                position="left"
                closeOnClickOutside={true}

                title={
                    <Group noWrap spacing='xs'>
                        <ActionIcon
                            size='lg'
                            variant="light"
                            radius='xl'
                            color="blue"
                            onClick={fnShow}
                        >
                            <IconArrowLeft size='1.25rem' stroke={1.5} />
                        </ActionIcon>

                        <Text>Tambah User</Text>
                    </Group>
                }
                size='md'

                styles={() => ({
                    title: {
                        fontWeight: 'bold',
                        textTransform: 'uppercase'
                    }
                })}
            >
                <InputComponent
                    stateId={data.userName.id}
                    stateWithAsterisk={data.userName.withAsterisk}
                    stateLabel={data.userName.label}
                    stateDescription={data.userName.description}
                    statePlaceHolder={data.userName.placeholder}
                    stateValue={data.userName.value}
                    stateError={data.userName.error}

                    stateMT="1rem"

                    fnOnChange={(e) => setData(d => ({
                        ...d,
                        userName: {
                            ...d.userName,
                            value: e.target.value
                        }
                    }))}
                />

                <InputWrapperCustom
                    stateLabel={data.userGender.label}
                    stateWithAsterisk={data.userGender.withAsterisk}
                    stateError={data.userGender.error}
                    stateDescription={data.userGender.description}
                    stateMT="1.5rem"
                >
                    <SegmentedControl
                        fullWidth
                        value={data.userGender.value}
                        onChange={(val) => setData(d => ({
                            ...d,
                            userGender: {
                                ...d.userGender,
                                value: val
                            }
                        }))}

                        data={[{ value: '1', label: 'Pria' }, { value: '0', label: 'Wanita' }]}
                    />
                </InputWrapperCustom>

                <InputComponent
                    stateId={data.userEmail.id}
                    stateWithAsterisk={data.userEmail.withAsterisk}
                    stateLabel={data.userEmail.label}
                    stateDescription={data.userEmail.description}
                    statePlaceHolder={data.userEmail.placeholder}
                    stateValue={data.userEmail.value}
                    stateError={data.userEmail.error}

                    stateMT="1rem"

                    fnOnChange={(e) => setData(d => ({
                        ...d,
                        userEmail: {
                            ...d.userEmail,
                            value: e.target.value
                        }
                    }))}
                />

                <InputWrapperCustom
                    stateLabel={data.userLevel.label}
                    stateWithAsterisk={data.userLevel.withAsterisk}
                    stateError={data.userLevel.error}
                    stateDescription={data.userLevel.description}
                    stateMT="1.5rem"
                >
                    <NativeSelect
                        value={data.userLevel.value}
                        onChange={(val) => {
                            setData(d => ({
                                ...d,
                                userLevel: { ...d.userLevel, value: val.target.value },
                                userSubLevel: { ...d.userSubLevel, value: subLevelSelector(val.target.value) }
                            }));
                        }}

                        data={Object.values(newRole).map(key => ({ value: key.slugLevel, label: key.caption }))}

                        styles={(tema) => ({
                            input: {
                                backgroundColor: tema.colorScheme === 'light' ? '#fff' : tema.colors.dark[6]
                            }
                        })}
                    />
                </InputWrapperCustom>

                <InputWrapperCustom
                    stateLabel={data.userSubLevel.label}
                    stateWithAsterisk={data.userSubLevel.withAsterisk}
                    stateError={data.userSubLevel.error}
                    stateDescription={data.userSubLevel.description}
                    stateMT="1.5rem"
                >
                    <NativeSelect
                        value={data.userSubLevel.value}
                        onChange={(val) => {
                            setData(d => ({ ...d, userSubLevel: { ...d.userSubLevel, value: val.target.value } }))
                        }}

                        data={subLevelList(data.userLevel.value)}

                        styles={(tema) => ({
                            input: {
                                backgroundColor: tema.colorScheme === 'light' ? '#fff' : tema.colors.dark[6]
                            }
                        })}
                    />
                </InputWrapperCustom>

                <InputComponent
                    stateId={data.userDefaultUsername.id}
                    stateWithAsterisk={data.userDefaultUsername.withAsterisk}
                    stateLabel={data.userDefaultUsername.label}
                    stateDescription={data.userDefaultUsername.description}
                    statePlaceHolder={data.userDefaultUsername.placeholder}
                    stateValue={data.userDefaultUsername.value}
                    stateError={data.userDefaultUsername.error}
                    stateRightSection={
                        <IconRefresh
                            size='1rem'
                            style={{ cursor: 'pointer' }}
                            color="#1864AB"

                            onClick={() => {
                                setData(d => ({
                                    ...d,
                                    userDefaultUsername: {
                                        ...d.userDefaultUsername,
                                        value: regexLimitChar(regexNoSpace(uuidv4()), utils.generateUsernameLength)
                                    }
                                }))
                            }}
                        />
                    }

                    stateMT="1.5rem"

                    fnOnChange={(e) => setData(d => ({
                        ...d,
                        userDefaultUsername: {
                            ...d.userDefaultUsername,
                            value: regexNoSpace(e.target.value)
                        }
                    }))}
                />

                <InputComponent
                    stateId={data.userDefaultPassword.id}
                    stateWithAsterisk={data.userDefaultPassword.withAsterisk}
                    stateLabel={data.userDefaultPassword.label}
                    stateDescription={data.userDefaultPassword.description}
                    statePlaceHolder={data.userDefaultPassword.placeholder}
                    stateValue={data.userDefaultPassword.value}
                    stateError={data.userDefaultPassword.error}
                    stateRightSection={
                        <IconRefresh
                            size='1rem'
                            style={{ cursor: 'pointer' }}
                            color="#1864AB"

                            onClick={() => {
                                setData(d => ({
                                    ...d,
                                    userDefaultPassword: {
                                        ...d.userDefaultPassword,
                                        value: regexLimitChar(regexNoSpace(uuidv4()), utils.generatePasswordLength)
                                    }
                                }))
                            }}
                        />
                    }

                    stateMT="1.5rem"

                    fnOnChange={(e) => setData(d => ({
                        ...d,
                        userDefaultPassword: {
                            ...d.userDefaultPassword,
                            value: regexNoSpace(e.target.value)
                        }
                    }))}
                />

                <Button
                    fullWidth
                    mt='1.5rem'
                    loading={data.submitLoading}
                    onClick={handleSubmit}
                >
                    Submit Data
                </Button>
            </Drawer>

            <ModalResponse
                stateShow={data.modalResponse.show}
                fnHide={() => setData(d => ({
                    ...d,
                    modalResponse: {
                        ...d.modalResponse,
                        show: false
                    }
                }))}

                stateHideOnClickOutSide={data.modalResponse.hideOnClickOutSide}
                stateType={data.modalResponse.type}
                stateContent={data.modalResponse.content}
            />
        </React.Fragment>
    )
}

export default SUDataUserAdd;